import React from 'react';
import PropTypes from 'prop-types';
import { Container, ProgressBar, Checkbox } from 'components/ui';
import { UserSelect } from 'components/users';
import { ButtonGroup, Button, Row, Col, Panel } from 'react-bootstrap';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import Select from 'react-select';
import { Tab, Wizard } from 'components/ui/form-wizard';
import _ from 'lodash';
import * as Api from 'api';
import * as CanvassingActions from 'actions/markers';
import { connect } from 'react-redux';
import { emit } from 'utils/sockets';

class MarkerUploadContainer extends React.Component {
  state = {
    show_data: true,
    loading: false,
    uploading: false,
    data: null,
    current_step: 0,
    column_mapping: [],
    marker_type_mapping: [],
    user_mapping: [],
    marker_upload_count: 0,
    failed_rows: [],
    override: false,
    hailtrace_marker_columns: [
      //[Label, Prop Name, Type, Required]
      ['Marker Type', 'type', 'string', true],
      ['Latitude', 'latitude', 'number', true],
      ['Longitude', 'longitude', 'number', true],
      ['Title', 'title', 'string', false],
      ['Username', 'username', 'string', false],
      ['Description', 'description', 'string', false],
      ['Date Created', 'created_timestamp', 'date', false],
      ['Date Updated', 'updated_timestamp', 'date', false],
    ],

  }
  handleMappingNext = mapping => {
    this.setState({ column_mapping: mapping, current_step: this.state.current_step + 1 });
  }
  handleMarkerTypeNext = mapping => {
    this.setState({ marker_type_mapping: mapping, current_step: this.state.current_step + 1 });
  }
  handleUserNext = mapping => {
    this.setState({ user_mapping: mapping, current_step: this.state.current_step + 1 });
  }
  loadFile = () => {
    const [ file ] = this.fileInput.files;
    if(!file) return;
    this.setState({ loading: true })
    XlsxPopulate.fromDataAsync(file)
      .then(workbook => {
        const data = workbook && workbook.sheet(0) ? workbook.sheet(0).usedRange().value() : null;
        this.setState({ data, error: null, loading: false });
      })
      .catch(error => this.setState({ error: { message: 'Error parsing this file. Are you sure it is xlsx?'}, loading: false }))
  }
  getColumn = (header, data, withHeader = false) => {
    if(!data) return [];

    const [ headers ] = data ? data : [[]];
    const colIdx = headers.indexOf(header);
    return data.slice(withHeader ? 0 : 1, data.length).map(row => row[colIdx]);
  }
  startUpload() {
    if(this.state.uploading) return;

    this.setState({ uploading: true, error: null });

    this.upload()
      .then(e => this.setState({ uploading: false }))
      .catch(e => this.setState({ uploading: false, error: e }))
  }
  convertRowToMarker(row, headers) {
    const {
      hailtrace_marker_columns,
      user_mapping,
      marker_type_mapping,
      column_mapping
    } = this.state;
    const marker = {};

    //First, populate marker properties with data.
    for(let item of column_mapping) {
      const [ header, data_prop_name ] = item;
      const colIdx = headers.indexOf(header);
      const [ label, prop_name, type, required ] = hailtrace_marker_columns.find(([ label, ht_prop_name ]) => ht_prop_name == data_prop_name);
      const value = row[colIdx];

      switch(prop_name) {
        case 'latitude': case 'longitude': {
          if(!marker.location) {
            marker.location = {
              type: 'Point',
              coordinates: []
            }
          }
          if(prop_name == 'longitude') marker.location.coordinates[0] = Number(value);
          if(prop_name == 'latitude') marker.location.coordinates[1] = Number(value);
          break;
        }
        default: {
          switch(type) {
            case 'string': {
              marker[prop_name] = String(value);
              break;
            }
            case 'date': {
              marker[prop_name] = XlsxPopulate.numberToDate(value);
              break;
            }
            case 'number': {
              marker[prop_name] = Number(value);
              break;
            }
            default: {
              marker[prop_name] = value;
            }
          }
        }
      }
    }

    //Second, set marker type.
    const marker_type = (marker_type_mapping.find(([ data_type, type ]) => data_type == marker.type) || [])[1];
    marker.type = marker_type;
    if(!marker.type) throw Error('You did not select marker types for every item in your spreadsheet.');

    //Third, set the creator id.
    const creator_id = (user_mapping.find(([ username, id ]) => username == marker.username) || [])[1];
    delete marker.username;
    marker.created_by = creator_id;
    if(!marker.created_by) delete marker.created_by;

    return { ...marker, override: this.state.override };
  }
  async upload() {
    const { data, uploading } = this.state;
    if(!data) return;

    const [ headers, ...rows ] = data;

    for(let i = 0; i < rows.length; i++) {
      let row = rows[i];
      try {
        const marker = this.convertRowToMarker(row, headers);
        await emit('create-marker', { data: { marker }, publish: false });
      }
      catch(ex) {
        this.setState({
          failed_rows: [
            ...this.state.failed_rows,
            [i, ex]
          ]
        });
      }
      this.setState({
        marker_upload_count: this.state.marker_upload_count + 1
      });
    }
    this.setState({ uploading: false });

  }
  render() {
    const {
      data,
      loading,
      error,
      current_step,
      hailtrace_marker_columns,
      show_data,
      column_mapping,
      uploading,
      marker_upload_count,
      failed_rows,
    } = this.state;

    const [ headers ] = data ? data : [[]];
    const [ marker_type_header ] = (column_mapping.find(([ header, prop_name ]) => prop_name == 'type') || []);
    const [ username_header ] = (column_mapping.find(([ header, prop_name ]) => prop_name == 'username') || []);
    return (
      <Container>
        <Row>
          <Col xs={12}>
            <Panel header={<h3>Data Preview</h3>}>
              {data ? (
                <div className='table-responsive'>
                  <table className='table table-condensed table-bordered'>
                    <thead>
                      <tr>
                        {/* <th><input type='checkbox' /></th> */}
                        {headers.map((item, idx) => (
                          <th style={{ whiteSpace: 'nowrap' }} key={`${item}-${idx}`}>{item}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.slice(1, 5).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {/* <td><input type='checkbox' /></td> */}
                          {row.map((item, colIdx) => <td style={{ maxWidth: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden'}} key={`${rowIdx}-${colIdx}`}>{item}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.length > 51 ? <p className='text-center alert alert-info'>Not all rows are shown. {data.length - 51} Rows hidden.</p> : null}
                </div>
              ) : null}
            </Panel>
            <Wizard
              onTab={idx => idx < current_step ? this.setState({ current_step: idx }) : null}
              shouldTab={(number) => false}
              tab={current_step || 0}
            >
              <Tab title='Select File'>
                {error ? <p className='alert alert-danger'>{error.message}</p> : null}
                {data ? <p>{data.length - 1} Rows</p> : null}
                <input onChange={event => this.loadFile()} id='file' name='file' ref={input => this.fileInput = input} style={{ opacity: 0, position: 'absolute'}} className='' type='file' />
                {!data ? (
                  <ButtonGroup>
                    <label className='btn btn-primary' id='file' htmlFor='file'><i className='fa fa-file'></i> Select File</label>
                  </ButtonGroup>
                ) : (
                  <ButtonGroup>
                    <label className='btn btn-default' id='file' htmlFor='file'><i className='fa fa-file'></i> Select File</label>
                    <Button onClick={e => this.setState({ current_step: current_step + 1 })} bsStyle='primary'>Next</Button>
                  </ButtonGroup>
                )}

                {!data ? (
                  <Panel className='panel-transparent'>
                    {!loading ? (
                      <p className='text-center'>Select a XLSX File to Begin</p>
                    ) : (
                      <h3 className='text-center'><i className='fa fa-spinner fa-spin' /></h3>
                    )}
                  </Panel>
                ) : null}
              </Tab>
              <Tab title='Column Mapping'>
                <ColumnMapper
                  mapping={this.state.column_mapping}
                  onNext={this.handleMappingNext}
                  allowed_columns={hailtrace_marker_columns}
                  headers={headers}
                />
              </Tab>
              <Tab title='Marker Types'>
                <MarkerTypeMapper
                  mapping={this.state.marker_type_mapping}
                  onNext={this.handleMarkerTypeNext}
                  data_marker_types={_.uniq(this.getColumn(marker_type_header, data))}
                />
              </Tab>
              {username_header ? (
                <Tab title='Users'>
                  <UserMapper
                    mapping={this.state.user_mapping}
                    onNext={this.handleUserNext}
                    usernames={_.uniq(this.getColumn(username_header, data))}
                  />
                </Tab>
              ) : null}
              <Tab title='Override Preference'>
                <div className='alert alert-info'>
                  <Row>
                    <Col md={10} xs={9}>
                      <div className='mb-md'>
                        <Checkbox
                          onChange={() => this.setState({ override: !this.state.override })}
                          checked={this.state.override}
                          label='Override Duplicate Data'
                        />
                      </div>
                      <p>
                        <span><i className='fa fa-info-circle mr-md' /></span>
                        Our system only allows for one marker per lattitude/longitude coordinate. If you would
                        prefer for your new data to override old data where any duplicates exist (lattitude and longitude are the same),
                        check the checkbox. Otherwise, any old markers will not be affected in the case of a duplicate corrdinate
                        being found.
                      </p>
                    </Col>
                    <Col md={2} xs={3}>
                      <div className='pull-right'>
                        <Button
                          bsStyle='success'
                          onClick={() => this.setState({ current_step: this.state.current_step + 1 })}
                          >
                          Next
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>
              <Tab title='Upload'>
                {uploading ? <ProgressBar percent={marker_upload_count / (data.length - 1)} /> : null}
                <div className={`alert alert-${ uploading ? 'warning' : 'info' }`}>
                  <h3>Upload Canvassing Markers</h3>
                  <p className='mb-lg'><strong>Please note:</strong> Do not close your browser window as these markers are being created.</p>
                  {!uploading ? <Button onClick={e => this.startUpload() } bsStyle='primary'>Upload Now</Button> : <Button bsStyle='default'>Processing...</Button>}
                </div>

                {failed_rows.length > 0 ? (
                  <div className='alert alert-danger'>
                    <div>
                      {failed_rows.reverse().map(([ idx, error ]) => (
                        <p className='animated fadeInLeft' key={idx}><strong>Row {idx}</strong> {error.message}</p>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Tab>
            </Wizard>
          </Col>
        </Row>
      </Container>
    );
  }
}

class ColumnMapper extends React.Component {
  static propTypes = {
    onNext: PropTypes.func.isRequired,
    headers: PropTypes.arrayOf(PropTypes.string),
    allowed_columns: PropTypes.array
  }
  state = {
    mapping: [
      //[ header, hailtrace prop name ]
    ]
  }
  isNotUsed = ([ label, prop_name, type, required ]) => {
    return this.state.mapping.find(([ header, h_prop_name ]) => h_prop_name == prop_name) ? false : true
  }
  getValue = prop_name => {
    const [ heading ] = this.state.mapping.find(([ header, hprop_name ]) => hprop_name == prop_name) || ['']
    return heading;
  }
  handleMapping = (data, prop_name, mapping) => {
    const clean_mapping = mapping.filter(([ header, h_prop_name ]) => h_prop_name !== prop_name);
    if(!data) {
      return this.setState({ mapping: clean_mapping })
    }
    this.setState({ mapping: [ ...clean_mapping, [ data.value, prop_name ]]})
  }
  componentDidMount() {
    const { headers, allowed_columns } = this.props;
    if(!headers || !allowed_columns) return;
    const mapping = this.props.mapping || [];

    if(mapping.length == 0) {
      headers.forEach(header => {
        const [ label, prop_name ] = allowed_columns.find(([ label ]) => label == header) || [];

        if(prop_name) {
          mapping.push([ header, prop_name ])
        }
      });
    }

    this.setState({ mapping });
  }
  render() {
    let { headers, allowed_columns } = this.props;
    const { mapping } = this.state;
    if(!headers || !allowed_columns) return null;
    const number_of_required = allowed_columns.filter(([ label, prop_name, data_type, required ]) => required).length;
    const number_of_required_mapped = allowed_columns.filter(([ label, prop_name, data_type, required ]) => this.getValue(prop_name) && required).length;
    return (
      <Row>
        <Col xs={12}>
          <div className='alert alert-info'>
            <div className='pull-right'>
              {number_of_required > number_of_required_mapped ? (
                <p><span className='label label-default'>{number_of_required - number_of_required_mapped}</span> required field{number_of_required - number_of_required_mapped > 1 ? 's' : null} left.</p>
              ) : (
                <Button onClick={e => this.props.onNext(mapping)} bsStyle='primary' className='animated bounceInLeft'>Next</Button>
              )}
            </div>
            <p>Select which columns match up.</p>
            <p>You may move to the next step once you map all of the required fields.</p>
          </div>
        </Col>
        <Col xs={12}>
          {allowed_columns.map(([ label, prop_name, data_type, required ], idx) => (
            <Row key={label + idx} className='mb-sm'>
              <Col xs={12} md={2}> {this.getValue(prop_name) ? <i className='text-primary fa fa-2x fa-check-circle animated bounceIn' /> : <i className='fa fa-2x fa-circle-o animated bounceIn' />}</Col>
              <Col xs={12} md={5}>{label} {required ? <span className='label label-primary'>Required</span> : null}</Col>
              <Col xs={12} md={5}>
                <Select
                  options={headers.map(label => ({ label, value: label }))}
                  onChange={data => this.handleMapping(data, prop_name, mapping)}
                  value={this.getValue(prop_name)}
                />
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
    )
  }
}

@connect(
  ({ canvassing }) => ({ ...canvassing }),
  CanvassingActions
)
class MarkerTypeMapper extends React.Component {
  static propTypes = {
    onNext: PropTypes.func.isRequired
  }
  state = {
    mapping: [
      //[Data Type, Existing Marker Type]
    ]
  }
  getValue = (data_marker_type, mapping) => {
    const [ dtype, marker_type ] = mapping.find(([ dtype, mtype ]) => dtype == data_marker_type) || [];
    return marker_type || '';
  }
  handleSelectMarkerType = (data_marker_type, marker_type, mapping) => {
    const clean_mapping = mapping.filter(([data_mt, mt]) => data_mt !== data_marker_type);
    if(!marker_type) {
      return this.setState({ mapping: clean_mapping });
    }
    return this.setState({
      mapping: [
        ...clean_mapping,
        [ data_marker_type, marker_type.key ]
      ]
    });
  }
  async componentDidMount() {
    const { mapping } = this.props;
    if(mapping) this.setState({ mapping });

    this.props.initMapMarkerTypes();
  }
  render() {
    const { data_marker_types, marker_types } = this.props;
    const { mapping } = this.state;
    const number_of_mapped_types = mapping.length;
    const number_of_data_types = data_marker_types.length;
    const number_left = number_of_data_types - number_of_mapped_types;
    const optionRenderer = option => (
      <Row className='clearfix'>
        <Col xs={1}>
          <img className='mt-sm img-responsive pull-left' src={`/assets/images/${option.url}`} />
        </Col>
        <Col xs={11}>
          <p className='pl-sm'>{option.name}</p>
        </Col>
      </Row>
    );
    return (
      <Row>
        <Col xs={12}>
          <div className='alert alert-info'>
            {number_left == 0 ? (
              <Button className='pull-right animated bounceInLeft' onClick={e => this.props.onNext(mapping)} bsStyle='primary'>Next</Button>
            ) : (
              <p className='pull-right'><span className='label label-default'>{number_left}</span> Left to Match</p>
            )}
            <p>Match your Spreadsheet Markers to your Hail Trace Markers.</p>
          </div>
        </Col>
        <Col xs={12}>
          {data_marker_types.map(dtype => (
            <Row key={dtype} className='mb-sm'>
              <Col xs={12} md={2}>
                {this.getValue(dtype, mapping) ? <i className='fa fa-2x fa-check-circle text-primary animated bounceIn' /> : <i className='fa fa-2x fa-circle-o' />}
              </Col>
              <Col xs={12} md={5}>
                {dtype}
              </Col>
              <Col xs={12} md={5}>
                <Select
                  filterOption={(option, filterString) => option.name.toLowerCase().indexOf(filterString.toLowerCase()) > -1}
                  options={marker_types}
                  valueKey='key'
                  valueRenderer={optionRenderer}
                  onChange={option => this.handleSelectMarkerType(dtype, option, mapping)}
                  optionRenderer={optionRenderer}
                  value={this.getValue(dtype, mapping)}
                />
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
    )
  }
}

class UserMapper extends React.Component {
  static propTypes = {
    onNext: PropTypes.func.isRequired
  }
  state = {
    mapping: [
      //[ spreadsheet username, hailtrace user id ]
    ]
  }
  getValue = (username, mapping) => {
    const [ m_username, id ] = mapping.find(([ m_username, m_id ]) => username == m_username) || [];
    return id || ''
  }
  handleSelectUser = (username, id, mapping) => {
    const clean_mapping = mapping.filter(([ m_username ]) => m_username !== username);
    if(!id) {
      return this.setState({ mapping: clean_mapping });
    }

    return this.setState({
      mapping: [ ...clean_mapping, [ username, id ]]
    });
  }
  componentDidMount() {
    const { mapping } = this.props;
    if(mapping) {
      this.setState({ mapping });
    }
  }
  render() {
    const { mapping } = this.state;
    const { usernames } = this.props;
    const usernames_count = usernames.length;
    const mapping_count = mapping.length;
    const left_count = usernames_count - mapping_count;

    return (
      <Row>
        <Col xs={12}>
          <div className='alert alert-info'>
            {left_count === 0 ? (
              <Button onClick={e => this.props.onNext(mapping)} bsStyle='primary' className='pull-right'>Next</Button>
            ) : (
              <p className='pull-right'><span className='label label-default'>{left_count}</span> Left</p>
            )}
            <p>Select the Hailtrace User for each Spreadsheet User.</p>
          </div>
        </Col>
        <Col xs={12}>
          <Row>
            <Col xs={12} md={2}></Col>
            <Col xs={12} md={5}><strong>Spreadsheet Username</strong></Col>
            <Col xs={12} md={5}><strong>HailTrace User</strong></Col>
          </Row>
          {usernames.map(name => (
            <Row key={name}>
              <Col xs={12} md={2}>
                {this.getValue(name, mapping) ? (
                  <i className='fa fa-2x fa-check-circle text-primary animated bounceIn' />
                ) : (
                  <i className='fa fa-2x fa-circle-o' />
                )}
              </Col>
              <Col xs={12} md={5}>{name}</Col>
              <Col xs={12} md={5}>
                <UserSelect
                  onChange={option => this.handleSelectUser(name, option && option.value || null, mapping)}
                  value={this.getValue(name, mapping)}
                />
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
    )
  }
}


export default MarkerUploadContainer;



// WEBPACK FOOTER //
// ./src/browser/components/clients/canvassing/marker-upload-container.js